/**
 * Handing the OPEN board tab the record MCP just wrote — before anything else
 * touches the boards nonce.
 *
 * ## The bug this exists to prevent, in full
 *
 * `TaskBoardTab` holds the open board in memory and persists it as a WHOLE
 * board: `persist()` calls `boardsStore.writeBoard(snapshot)`, i.e.
 * `extension_records_sync_scope`, which **tombstones every record in the scope
 * that is absent from the array it is given**. A record-granular MCP write is
 * invisible to that snapshot, so the tab's next persist deletes it.
 *
 * Bumping the nonce does not fix that — it CAUSES it. The nonce drives
 * `reconcileFromDisk`, whose first action is `flushSave()` ("so our own writes
 * are never lost to the re-read"), and that flush is what writes the stale
 * snapshot. So the ordering is not a nicety:
 *
 *     publish the record into the open tab's snapshot  →  then bump
 *
 * `publishBoardChanges` → `subscribeBoardChanges` → `patchBoardWithRecords` is
 * the mechanism the app already built for exactly this (the team-board
 * realtime fast path); MCP reuses it rather than inventing a second one.
 *
 * ## Why the payload rides on the result object, under a Symbol
 *
 * `refreshUiAfterTool(tool, result)` is called by `runHandler` on the success
 * path only, so the handler has to get the records to it somehow, and a
 * module-level queue would survive a failed call and republish stale records
 * into the next one. A `Symbol` key cannot collide with a wire field and is
 * dropped by `JSON.stringify`, so even if the refresh step were skipped
 * entirely this can never reach a connected client as part of
 * `structuredContent`. That property is pinned by a test.
 */

/** Not exported to the wire by construction: symbols do not serialise. */
export const BOARD_UI_RECORDS = Symbol('mcp.boardUiRecords');

/**
 * Attach the records to publish to a handler's result. `records` are already in
 * the flat wire shape (`boardRecords.toWireRecord`).
 */
export function stageBoardRecords(result, scopeKey, records) {
  if (!result || typeof result !== 'object' || !records?.length) return result;
  const existing = result[BOARD_UI_RECORDS];
  result[BOARD_UI_RECORDS] = existing
    ? { scopeKey, records: [...existing.records, ...records] }
    : { scopeKey, records };
  return result;
}

/** What `uiRefresh` publishes, or null. */
export function takeBoardRecords(result) {
  if (!result || typeof result !== 'object') return null;
  return result[BOARD_UI_RECORDS] || null;
}
